import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiFileText, FiChevronRight, FiMail, FiPhone } from 'react-icons/fi';
import Logo from '../../images/AE-Hero.svg';

const sections = [
  { id: 'acceptance',     label: 'Acceptance of Terms' },
  { id: 'services',       label: 'Our Services' },
  { id: 'accounts',       label: 'Accounts & Registration' },
  { id: 'quotations',     label: 'Quotations & Pricing' },
  { id: 'obligations',    label: 'Client Obligations' },
  { id: 'ip',             label: 'Intellectual Property' },
  { id: 'liability',      label: 'Limitation of Liability' },
  { id: 'warranty',       label: 'Warranties & Guarantees' },
  { id: 'termination',    label: 'Termination' },
  { id: 'governing',      label: 'Governing Law' },
  { id: 'changes',        label: 'Changes to These Terms' },
  { id: 'contact',        label: 'Contact Us' },
];

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (typeof window !== 'undefined') {
      const AOS = require('aos');
      AOS.refresh();
    }
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* ── Hero banner ── */}
      <div
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 60%, #2b6cb0 100%)',
          paddingTop: 'calc(80px + 3rem)',
          paddingBottom: '5rem',
        }}
      >
        {/* Decorative rings */}
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full border border-white opacity-5" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full border border-white opacity-5" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 rounded-full border border-white opacity-5" />

        <div className="container mx-auto px-4 relative z-10 text-center" data-aos="fade-up">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center p-2 shadow-lg">
              <img src={Logo} alt="Alternative Energy" className="max-h-full max-w-full object-contain" />
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white bg-opacity-10 border border-white border-opacity-20 text-white text-sm font-medium mb-5">
            <FiFileText className="text-yellow-400" />
            Legal Document
          </div>

          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'var(--heading-font)' }}
          >
            Terms of Service
          </h1>
          <p className="text-blue-200 max-w-2xl mx-auto text-lg mb-6">
            Please read these terms carefully before using our platform or engaging
            Alternative Energy's services.
          </p>

          {/* Breadcrumb */}
          <nav className="flex justify-center items-center gap-2 text-sm text-blue-300">
            <Link to="/" className="hover:text-white transition-colors duration-200">Home</Link>
            <FiChevronRight className="text-blue-400" />
            <span className="text-white font-medium">Terms of Service</span>
          </nav>

          <p className="mt-6 text-blue-300 text-sm">
            Effective date: <span className="text-white font-medium">1 January 2024</span>
            &nbsp;·&nbsp; Last updated: <span className="text-white font-medium">6 April 2026</span>
          </p>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="light-background min-h-screen">
        <div className="container mx-auto px-4 py-16">
          <div className="flex flex-col lg:flex-row gap-10 max-w-7xl mx-auto">

            {/* ── Sticky sidebar ── */}
            <aside className="lg:w-72 flex-shrink-0" data-aos="fade-right">
              <div
                className="bg-white rounded-2xl shadow-md p-6 lg:sticky lg:top-28"
                style={{ borderTop: '4px solid var(--accent-color)' }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-4"
                  style={{ color: 'var(--accent-color)' }}
                >
                  Table of Contents
                </p>
                <ul className="space-y-1">
                  {sections.map((s, i) => (
                    <li key={s.id}>
                      <button
                        onClick={() => scrollTo(s.id)}
                        className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg text-sm transition-all duration-200"
                        style={{ color: 'var(--default-color)' }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = 'color-mix(in srgb, var(--accent-color), transparent 90%)';
                          e.currentTarget.style.color = 'var(--accent-color)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = '';
                          e.currentTarget.style.color = 'var(--default-color)';
                        }}
                      >
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white"
                          style={{ background: 'var(--accent-color)', opacity: 0.85 }}
                        >
                          {i + 1}
                        </span>
                        {s.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Quick contact card */}
                <div
                  className="mt-6 rounded-xl p-4"
                  style={{ background: 'color-mix(in srgb, var(--accent-color), transparent 90%)' }}
                >
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--accent-color)' }}>
                    Questions?
                  </p>
                  <a
                    href="mailto:info@alternative.co.sz"
                    className="flex items-center gap-2 text-sm mb-2 transition-colors duration-200"
                    style={{ color: 'var(--heading-color)' }}
                  >
                    <FiMail className="flex-shrink-0" style={{ color: 'var(--accent-color)' }} />
                    info@alternative.co.sz
                  </a>
                  <a
                    href="tel:+26825185414"
                    className="flex items-center gap-2 text-sm transition-colors duration-200"
                    style={{ color: 'var(--heading-color)' }}
                  >
                    <FiPhone className="flex-shrink-0" style={{ color: 'var(--accent-color)' }} />
                    +268 2518 5414
                  </a>
                </div>
              </div>
            </aside>

            {/* ── Main content ── */}
            <main className="flex-1 min-w-0" data-aos="fade-up">

              {/* Intro card */}
              <div
                className="bg-white rounded-2xl shadow-sm p-8 mb-8"
                style={{ borderLeft: '4px solid var(--accent-color)' }}
              >
                <p className="text-gray-600 leading-relaxed">
                  These Terms of Service (<strong>"Terms"</strong>) govern your access to and use of the website,
                  client portal, and services provided by <strong>Alternative Energy (Pty) Ltd</strong> (
                  <strong>"Alternative Energy"</strong>, <strong>"we"</strong>, <strong>"us"</strong>, or{' '}
                  <strong>"our"</strong>), a company registered in Eswatini. By accessing our platform or
                  engaging our services, you confirm that you have read, understood, and agree to be bound by
                  these Terms. If you do not agree, please do not use our platform or services.
                </p>
              </div>

              <div className="space-y-8">

                {/* 1 – Acceptance */}
                <ToSSection id="acceptance" number="1" title="Acceptance of Terms">
                  <p>
                    By creating an account, submitting a quotation request, or otherwise using any part of our
                    platform or services, you represent that:
                  </p>
                  <BulletList items={[
                    'You are at least 18 years of age or have the legal capacity to enter into a binding agreement.',
                    'You have the authority to bind yourself or the organisation you represent to these Terms.',
                    'All information you provide to us is accurate, current, and complete.',
                    'You will comply with all applicable laws and regulations in Eswatini and any other jurisdiction relevant to your use of our services.',
                  ]} />
                  <p>
                    These Terms, together with our Privacy Policy and any quotation or service agreement we enter
                    into with you, constitute the entire agreement between you and Alternative Energy.
                  </p>
                </ToSSection>

                {/* 2 – Services */}
                <ToSSection id="services" number="2" title="Our Services">
                  <p>
                    Alternative Energy provides integrated energy solutions to residential, commercial, and industrial
                    clients across Eswatini, including but not limited to:
                  </p>

                  <div className="grid sm:grid-cols-3 gap-4 my-4">
                    {[
                      {
                        icon: '☀️',
                        title: 'Solar Services',
                        items: ['System design & installation', 'Rooftop & ground-mount arrays', 'Hybrid & off-grid systems', 'Solar maintenance & cleaning'],
                      },
                      {
                        icon: '⚡',
                        title: 'Electrical Services',
                        items: ['Rewiring & panel upgrades', 'Distribution board installation', 'LED lighting fit-outs', 'Overhead line & pole work'],
                      },
                      {
                        icon: '🔋',
                        title: 'Generator Services',
                        items: ['Supply & installation', 'Maintenance & servicing', 'Generator hire & rental', 'Automatic transfer switches'],
                      },
                    ].map(col => (
                      <div
                        key={col.title}
                        className="bg-white border rounded-xl p-4"
                        style={{ borderColor: 'color-mix(in srgb, var(--accent-color), transparent 70%)' }}
                      >
                        <p className="text-2xl mb-2">{col.icon}</p>
                        <p className="font-semibold text-sm mb-3" style={{ color: 'var(--heading-color)' }}>{col.title}</p>
                        <ul className="space-y-1">
                          {col.items.map(item => (
                            <li key={item} className="flex items-start gap-2 text-xs text-gray-500">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: 'var(--accent-color)' }} />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>

                  <p>
                    We reserve the right to modify, suspend, or discontinue any service or feature of our platform
                    at any time with reasonable notice. We are not liable to you or any third party for any such
                    modification, suspension, or discontinuation.
                  </p>
                  <InfoBox>
                    All services are subject to site assessment and technical feasibility. A quotation does not
                    constitute a guarantee that services can be delivered at the estimated cost until a site visit
                    has been completed.
                  </InfoBox>
                </ToSSection>

                {/* 3 – Accounts */}
                <ToSSection id="accounts" number="3" title="Accounts & Registration">
                  <p>
                    To access our client portal and submit quotation requests, you are required to create an account.
                    When registering, you agree to:
                  </p>
                  <BulletList items={[
                    'Provide accurate, current, and complete information during registration.',
                    'Maintain and promptly update your account information to keep it accurate and complete.',
                    'Keep your password confidential and not share it with any third party.',
                    'Notify us immediately at info@alternative.co.sz if you suspect any unauthorised use of your account.',
                    'Accept responsibility for all activities that occur under your account.',
                  ]} />
                  <p>
                    We reserve the right to suspend or terminate accounts that we reasonably suspect contain
                    inaccurate information, violate these Terms, or are being used for fraudulent or unlawful purposes.
                  </p>
                  <SubHeading>Social Login</SubHeading>
                  <p>
                    If you register using a third-party service (Google, GitHub, or Facebook), you authorise us to
                    access and store certain profile information from that provider in accordance with our
                    Privacy Policy. Your use of those third-party services is also governed by their respective
                    terms and privacy policies.
                  </p>
                </ToSSection>

                {/* 4 – Quotations */}
                <ToSSection id="quotations" number="4" title="Quotations & Pricing">
                  <SubHeading>Quotation Requests</SubHeading>
                  <p>
                    Submitting a quotation request through our platform does not create a binding contract. A
                    quotation becomes binding only when both parties have signed a formal service agreement or
                    accepted a written quote issued by Alternative Energy.
                  </p>
                  <SubHeading>Validity of Quotes</SubHeading>
                  <p>
                    Unless otherwise stated in writing, all quotations issued by Alternative Energy are valid for
                    <strong> 30 days</strong> from the date of issue. After this period, pricing may be subject to
                    revision due to changes in material costs, exchange rates, or labour availability.
                  </p>
                  <SubHeading>Pricing & Estimates</SubHeading>
                  <BulletList items={[
                    'All prices are quoted in Eswatini Lilangeni (SZL) and are inclusive of VAT unless explicitly stated otherwise.',
                    'Online solar calculator estimates are indicative only and may differ from the final quoted price following a site assessment.',
                    'Additional costs may arise from unforeseen site conditions discovered during installation, which will be communicated and agreed upon before proceeding.',
                    'A deposit may be required before commencement of work, as specified in the formal service agreement.',
                  ]} />
                  <SubHeading>Payment Terms</SubHeading>
                  <p>
                    Payment terms will be set out in the formal service agreement. Unless otherwise agreed, invoices
                    are due within <strong>14 days</strong> of issue. Late payments may attract interest at the
                    prevailing prime lending rate. We reserve the right to suspend work on outstanding projects
                    where payment obligations are not met.
                  </p>
                </ToSSection>

                {/* 5 – Client Obligations */}
                <ToSSection id="obligations" number="5" title="Client Obligations">
                  <p>
                    To enable us to deliver our services effectively and safely, you agree to:
                  </p>
                  <BulletList items={[
                    'Provide accurate site information, including property dimensions, existing electrical infrastructure, and any known hazards.',
                    'Ensure safe and unobstructed access to your property for our team during agreed-upon installation or maintenance windows.',
                    'Obtain any necessary permits, approvals, or consents from relevant authorities (e.g. Eswatini Electricity Company, local councils) unless we have explicitly agreed in writing to do so on your behalf.',
                    'Ensure that all persons on site during our work are authorised to be there and that the work area complies with applicable health and safety regulations.',
                    'Not interfere with, modify, or tamper with any installed equipment without prior written consent from Alternative Energy.',
                    'Promptly report any faults, damage, or safety concerns relating to installed systems to our support team.',
                  ]} />
                  <InfoBox>
                    Failure to meet these obligations may result in delays, additional costs, or voiding of
                    applicable warranties. Alternative Energy accepts no liability for issues arising from
                    inaccurate information provided by the client.
                  </InfoBox>
                </ToSSection>

                {/* 6 – Intellectual Property */}
                <ToSSection id="ip" number="6" title="Intellectual Property">
                  <p>
                    All content, designs, logos, software, documentation, and materials on our platform and in our
                    service deliverables are the intellectual property of Alternative Energy or our licensors, and
                    are protected by applicable copyright, trademark, and other intellectual property laws.
                  </p>
                  <SubHeading>What You May Do</SubHeading>
                  <BulletList items={[
                    'Access and use our platform for personal or business purposes in accordance with these Terms.',
                    'Download or print quotation documents or reports generated for your account for your own reference.',
                  ]} />
                  <SubHeading>What You May Not Do</SubHeading>
                  <BulletList items={[
                    'Copy, reproduce, distribute, or create derivative works from any platform content without our prior written consent.',
                    'Remove or alter any copyright, trademark, or other proprietary notices.',
                    'Use our branding, logos, or trade names without express written permission.',
                    'Reverse-engineer, decompile, or attempt to extract the source code of any software forming part of our platform.',
                  ]} />
                  <p>
                    Any feedback, suggestions, or ideas you submit to us may be used by Alternative Energy freely
                    and without obligation to you.
                  </p>
                </ToSSection>

                {/* 7 – Liability */}
                <ToSSection id="liability" number="7" title="Limitation of Liability">
                  <p>
                    To the fullest extent permitted by the laws of Eswatini, Alternative Energy shall not be liable
                    for any indirect, incidental, special, consequential, or punitive damages, including but not
                    limited to:
                  </p>
                  <BulletList items={[
                    'Loss of profits, revenue, or anticipated savings.',
                    'Loss of data or business interruption.',
                    'Damage caused by third-party products or materials integrated into our installations at the client\'s request.',
                    'Losses arising from events beyond our reasonable control (force majeure), including natural disasters, power outages, civil unrest, or supplier failures.',
                  ]} />
                  <p>
                    Our total aggregate liability to you in connection with any service or claim under these Terms
                    shall not exceed the total amount paid by you to Alternative Energy in the three (3) months
                    preceding the event giving rise to the claim.
                  </p>
                  <InfoBox>
                    Nothing in these Terms limits our liability for death or personal injury caused by our
                    negligence, fraud, or any other liability that cannot be excluded by law.
                  </InfoBox>
                </ToSSection>

                {/* 8 – Warranty */}
                <ToSSection id="warranty" number="8" title="Warranties & Guarantees">
                  <SubHeading>Workmanship Warranty</SubHeading>
                  <p>
                    Alternative Energy warrants that all installation and electrical work will be carried out by
                    qualified and certified personnel in a professional manner, in accordance with applicable
                    Eswatini electrical standards and regulations. Our workmanship warranty period will be specified
                    in your formal service agreement, typically <strong>12 months</strong> from the date of
                    commissioning.
                  </p>
                  <SubHeading>Equipment & Product Warranties</SubHeading>
                  <p>
                    Equipment supplied by Alternative Energy (solar panels, inverters, batteries, generators, etc.)
                    is covered by the manufacturer's warranty. We will assist you in pursuing warranty claims with
                    manufacturers but cannot be held liable for delays or decisions made by third-party manufacturers.
                  </p>
                  <SubHeading>Warranty Exclusions</SubHeading>
                  <BulletList items={[
                    'Damage caused by misuse, unauthorised modifications, or failure to follow manufacturer guidelines.',
                    'Normal wear and tear or deterioration over time.',
                    'Damage resulting from power surges, lightning strikes, flooding, or other force majeure events.',
                    'Faults arising from third-party interference with installed systems.',
                  ]} />
                </ToSSection>

                {/* 9 – Termination */}
                <ToSSection id="termination" number="9" title="Termination">
                  <SubHeading>Termination by You</SubHeading>
                  <p>
                    You may close your account at any time by contacting us at info@alternative.co.sz. Closure of
                    your account does not relieve you of any outstanding payment obligations or affect any ongoing
                    service agreements already in place.
                  </p>
                  <SubHeading>Termination by Us</SubHeading>
                  <p>
                    We reserve the right to suspend or terminate your account and access to our platform with
                    reasonable notice if:
                  </p>
                  <BulletList items={[
                    'You breach any provision of these Terms and fail to remedy the breach within 14 days of written notice.',
                    'You provide false or misleading information during registration or in connection with a quotation.',
                    'You engage in fraudulent, abusive, or unlawful conduct on our platform.',
                    'We are required to do so by applicable law or regulatory order.',
                  ]} />
                  <SubHeading>Effect of Termination</SubHeading>
                  <p>
                    Upon termination, your right to access the client portal will cease immediately. Provisions of
                    these Terms that by their nature should survive termination (including payment obligations,
                    intellectual property rights, and limitation of liability) will continue to apply.
                  </p>
                </ToSSection>

                {/* 10 – Governing Law */}
                <ToSSection id="governing" number="10" title="Governing Law">
                  <p>
                    These Terms are governed by and construed in accordance with the laws of the Kingdom of
                    Eswatini. Any disputes arising out of or in connection with these Terms shall be subject to
                    the exclusive jurisdiction of the courts of Eswatini.
                  </p>
                  <p>
                    Where a dispute cannot be resolved through direct negotiation, both parties agree to attempt
                    mediation in good faith before initiating formal legal proceedings. The costs of mediation will
                    be shared equally unless otherwise agreed.
                  </p>
                  <InfoBox>
                    If you are a consumer, you may also have rights under applicable consumer protection laws in
                    Eswatini that cannot be waived by these Terms.
                  </InfoBox>
                </ToSSection>

                {/* 11 – Changes */}
                <ToSSection id="changes" number="11" title="Changes to These Terms">
                  <p>
                    We may revise these Terms from time to time to reflect changes to our services, business
                    practices, or applicable law. When we make material changes, we will:
                  </p>
                  <BulletList items={[
                    'Update the "Last updated" date at the top of this page.',
                    'Post a notice on our website for at least 30 days before the changes take effect.',
                    'Where required, notify registered users via email.',
                  ]} />
                  <p>
                    Your continued use of our platform or services after the effective date of any revised Terms
                    constitutes your acceptance of those changes. If you do not agree with the revised Terms, you
                    must stop using our platform and services.
                  </p>
                </ToSSection>

                {/* 12 – Contact */}
                <ToSSection id="contact" number="12" title="Contact Us">
                  <p>
                    If you have any questions about these Terms of Service, or wish to exercise any right or make
                    a complaint, please reach out to us:
                  </p>

                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-3">
                      <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--accent-color)' }}>
                        Registered Address
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        Alternative Energy (Pty) Ltd<br />
                        Lot 810 Cnr Lifahlawane St & Shiselweni Ave<br />
                        Matsapha M202<br />
                        Eswatini
                      </p>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-3">
                      <p className="font-bold text-sm uppercase tracking-wider" style={{ color: 'var(--accent-color)' }}>
                        Contact Details
                      </p>
                      <a
                        href="mailto:info@alternative.co.sz"
                        className="flex items-center gap-2 text-sm transition-colors duration-200"
                        style={{ color: 'var(--accent-color)' }}
                      >
                        <FiMail /> info@alternative.co.sz
                      </a>
                      <a
                        href="tel:+26825185414"
                        className="flex items-center gap-2 text-sm transition-colors duration-200"
                        style={{ color: 'var(--accent-color)' }}
                      >
                        <FiPhone /> +268 2518 5414
                      </a>
                      <a
                        href="tel:+26825187424"
                        className="flex items-center gap-2 text-sm transition-colors duration-200"
                        style={{ color: 'var(--accent-color)' }}
                      >
                        <FiPhone /> +268 2518 7424
                      </a>
                    </div>
                  </div>

                  <InfoBox className="mt-6">
                    We aim to respond to all enquiries regarding these Terms within{' '}
                    <strong>5 business days</strong>.
                  </InfoBox>
                </ToSSection>

              </div>

              {/* Footer nav */}
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-400">
                  © {new Date().getFullYear()} Alternative Energy (Pty) Ltd. All rights reserved.
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <Link
                    to="/privacy-policy"
                    className="transition-colors duration-200"
                    style={{ color: 'var(--accent-color)' }}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/"
                    className="transition-colors duration-200"
                    style={{ color: 'var(--accent-color)' }}
                  >
                    ← Back to Home
                  </Link>
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="px-4 py-2 rounded-lg text-white text-sm font-medium transition-all duration-200 hover:shadow-md"
                    style={{ background: 'var(--accent-color)' }}
                  >
                    ↑ Back to Top
                  </button>
                </div>
              </div>

            </main>
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Shared sub-components ── */

const ToSSection = ({ id, number, title, children }) => (
  <div
    id={id}
    className="bg-white rounded-2xl shadow-sm p-8 scroll-mt-28"
    data-aos="fade-up"
  >
    <div className="flex items-center gap-3 mb-5">
      <span
        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
        style={{ background: 'var(--accent-color)' }}
      >
        {number}
      </span>
      <h2
        className="text-xl font-bold m-0"
        style={{ color: 'var(--heading-color)', fontFamily: 'var(--heading-font)' }}
      >
        {title}
      </h2>
    </div>
    <div className="text-gray-600 leading-relaxed space-y-4 text-sm md:text-base">
      {children}
    </div>
  </div>
);

const SubHeading = ({ children }) => (
  <p className="font-semibold mt-4 mb-2" style={{ color: 'var(--heading-color)' }}>
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 ml-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span
          className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
          style={{ background: 'var(--accent-color)' }}
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const InfoBox = ({ children, className = '' }) => (
  <div
    className={`rounded-xl p-4 text-sm ${className}`}
    style={{
      background: 'color-mix(in srgb, var(--accent-color), transparent 90%)',
      borderLeft: '3px solid var(--accent-color)',
      color: 'var(--heading-color)',
    }}
  >
    {children}
  </div>
);

export default TermsOfService;